import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select, { components as SelectComponents } from "react-select";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarRange,
  CheckCircle2,
  Clock3,
  CreditCard,
  IndianRupee,
  Search,
  Wallet,
  Users,
  ChevronRight,
  X,
} from "lucide-react";

import "../../../../style/PaymentTracking.css";
import { apiClient } from "../../../../api/apiClient";
import Swal from "sweetalert2";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CaseItem {
  paymentId: number;
  id: string;
  orderId?: string;
  desc: string;
  qty: number;
  amount: number;
  date: string;

  rawDate: string | null; // ISO (yyyy-mm-dd) used for date range filtering
  status: "Paid" | "Pending";
}

interface Person {
  userId: number;
  roleId: number;

  name: string;
  role: string;

  completed: number;
  payable: number;
  paid: number;
  pending: number;

  cases: CaseItem[];
}

interface PaymentTrackingUser {
  userID: number;
  name: string;
  roleID: number;
}

function groupPaymentRows(rows: any[]): Person[] {
  const map = new Map < string, Person> ();

  rows.forEach((row) => {
    const key = `${row.userID}_${row.roleID}`;

    if (!map.has(key)) {
      map.set(key, {
        userId: row.userID,
        roleId: row.roleID,

        name: row.userName,
        role: row.roleName,

        completed: 0,
        payable: 0,
        paid: 0,
        pending: 0,

        cases: [],
      });
    }

    const person = map.get(key)!;

    const amount = Number(row.payableAmount);

    person.completed += 1;
    person.payable += amount;

    if (String(row.paymentStatus).toUpperCase() === "PAID") {
      person.paid += amount;
    } else {
      person.pending += amount;
    }

    person.cases.push({
      paymentId: row.workPaymentID,

      id: String(row.caseID),

      orderId: row.orderID,

      desc: row.materialName ?? "-",

      qty: 1,

      amount: amount,

      date: row.completedOn
        ? new Date(row.completedOn).toLocaleDateString("en-IN")
        : "-",

      rawDate: row.completedOn
        ? toISODate(new Date(row.completedOn))
        : null,

      status:
        String(row.paymentStatus).toUpperCase() === "PAID"
          ? "Paid"
          : "Pending",
    });
  });

  return Array.from(map.values());
}

const ROLES = ["All", "Designer", "Milling Hub", "Delivery"] as const;

type RoleFilter = (typeof ROLES)[number];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function money(n: number): string {
  return "₹" + n.toLocaleString("en-IN");
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function getRoleName(roleId: number | null | undefined) {
  switch (roleId) {
    case 3:
      return "Milling Hub";

    case 6:
      return "Designer";

    case 7:
      return "Delivery";

    default:
      return "";
  }
}

const getCaseWorkPaymentTracking = async (
  startDate?: string,
  endDate?: string
) => {
  const response = await apiClient.get < any > ("/Payment/payment-tracking", {
    params: {
      StartDate: startDate || undefined,
      EndDate: endDate || undefined,
    },
  });

  return response.data;
};

const getPaymentTrackingUsers = async () => {
  const response = await apiClient.get < any > (
    "/Payment/payment-tracking/users"
  );

  return response.data;
};

function getRoleClass(role: string) {
  switch (role) {
    case "Designer":
      return "designer";

    case "Milling Hub":
      return "milling";

    case "Delivery":
      return "delivery";

    default:
      return "";
  }
}

// Formats a JS Date as yyyy-mm-dd using local date parts (avoids UTC
// off-by-one shifts that toISOString() can introduce).
function toISODate(d: Date | null): string {
  if (!d) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

// ---------------------------------------------------------------------------
// Custom react-select renderers
// ---------------------------------------------------------------------------
// react-select's default Option / SingleValue renderers don't set a
// `title` attribute, so when the label text is wider than the box and
// gets truncated (via CSS ellipsis), there's nothing to show the full
// text on hover. These thin wrappers keep all of react-select's default
// rendering/classnames (by delegating to `components.Option` /
// `components.SingleValue`) and simply add `title={data.label}` so the
// browser shows the full text as a native tooltip on hover.

function SelectOptionWithTitle(props: any) {
  return (
    <SelectComponents.Option
      {...props}
      innerProps={{ ...props.innerProps, title: props.data.label }}
    >
      {props.children}
    </SelectComponents.Option>
  );
}

function SelectSingleValueWithTitle(props: any) {
  return (
    <SelectComponents.SingleValue
      {...props}
      innerProps={{ ...props.innerProps, title: props.data.label }}
    >
      {props.children}
    </SelectComponents.SingleValue>
  );
}

const nameSelectComponents = {
  Option: SelectOptionWithTitle,
  SingleValue: SelectSingleValueWithTitle,
};

// ---------------------------------------------------------------------------
// DataTable style overrides (fixes columns shrinking on browser zoom)
// ---------------------------------------------------------------------------
// react-data-table-component lays out columns with flexbox. By default,
// cells are allowed to flex-shrink. If the table's container becomes
// narrower than the sum of the column widths — which happens when the
// page is zoomed out with Ctrl/Cmd + "-" — every column silently
// squashes instead of the table scrolling horizontally.
//
// Fix: give the table a minWidth, stop cells from shrinking, and (below,
// on the column definitions) mark genuinely fixed-width columns with
// `grow: 0` so they hold their width. Each DataTable is also wrapped in
// a horizontally-scrollable container, so once the real content can't
// fit, the user gets a scrollbar instead of squeezed columns.

const noShrinkTableStyles = {
  table: {
    style: {
      minWidth: "900px",
    },
  },
  headCells: {
    style: {
      whiteSpace: "nowrap",
      flexShrink: 0,
    },
  },
  cells: {
    style: {
      flexShrink: 0,
    },
  },
};

const teamTableStyles = {
  table: {
    style: {
      width: "100%",
      minWidth: "0",
    },
  },

  headCells: {
    style: {
      whiteSpace: "nowrap",
      flexShrink: 0,
    },
  },

  cells: {
    style: {
      flexShrink: 0,
    },
  },
};

// const teamTableStyles = noShrinkTableStyles("900px");
// const caseTableStyles = noShrinkTableStyles("760px");
// const modalTableStyles = noShrinkTableStyles("680px");

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PaymentTracking() {
  const [people, setPeople] = useState < Person[] > ([]);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState < PaymentTrackingUser[] > ([]);

  useEffect(() => {
    loadPaymentTracking();
    loadPaymentTrackingUsers();
  }, []);

  
  const formatPeriodDate = (value?: string | null) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const loadPaymentTracking = async (
    startDateParam?: string,
    endDateParam?: string
  ) => {
    try {
      setLoading(true);

      const rows = await getCaseWorkPaymentTracking(
        startDateParam,
        endDateParam
      );

      console.log("PAYMENT TRACKING ROWS:", rows);
      console.log("PAYMENT TRACKING ROW COUNT:", rows.length);

      const grouped = groupPaymentRows(rows);

      console.log("PAYMENT TRACKING GROUPED:", grouped);

      setPeople(grouped);
    } catch (error) {
      console.error("Payment tracking API error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentTrackingUsers = async () => {
    try {
      const users = await getPaymentTrackingUsers();

      console.log("PAYMENT TRACKING USERS:", users);

      setAllUsers(users);
    } catch (error) {
      console.error("Payment tracking users API error:", error);
    }
  };

  const [view, setView] = useState < "list" | "detail" > ("list");
  const [selectedId, setSelectedId] = useState < string | null > (null);
  const [roleFilter, setRoleFilter] = useState < RoleFilter > ("All");
  const [selectedName, setSelectedName] = useState("");
  const [search, setSearch] = useState("");

  const nameOptions = useMemo(() => {
    const filteredUsers =
      roleFilter === "All"
        ? allUsers
        : allUsers.filter((u) => getRoleName(u.roleID) === roleFilter);

    return filteredUsers
      .map((u) => ({
        value: `${u.userID}_${u.roleID}`,
        label: u.name || "",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allUsers, roleFilter]);

  // -------------------------------------------------------------------------
  // DATE RANGE FILTER STATE (single react-datepicker range picker)
  // -------------------------------------------------------------------------

  const [dateRange, setDateRange] = useState < [Date | null, Date | null] > ([
    null,
    null,
  ]);

  const [rangeStart, rangeEnd] = dateRange;

  // ISO strings derived from the picker, used for actual filtering.
  const startDate = toISODate(rangeStart);
  const endDate = toISODate(rangeEnd);

  function clearDateRange() {
    setDateRange([null, null]);

    // Reload default latest 7 days
    loadPaymentTracking();
  }

  // -------------------------------------------------------------------------
  // MODAL STATE
  // -------------------------------------------------------------------------

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [modalPerson, setModalPerson] = useState < Person | null > (null);

  // -------------------------------------------------------------------------
  // Filtered People
  // -------------------------------------------------------------------------

  const filtered = useMemo(() => {
    let result =
      roleFilter === "All"
        ? people
        : people.filter((p) => p.role === roleFilter);

    // ---------------------------------------------------------
    // NAME DROPDOWN FILTER
    // ---------------------------------------------------------
    if (selectedName) {
      result = result.filter(
        (p) => `${p.userId}_${p.roleId}` === selectedName
      );
    }

    // ---------------------------------------------------------
    // GLOBAL SEARCH
    // Searches person + role + all payment/case information
    // ---------------------------------------------------------
    if (search.trim()) {
      const query = search.toLowerCase().trim();

      result = result.filter((p) => {
        // Person-level fields
        const personMatch =
          String(p.userId).toLowerCase().includes(query) ||
          String(p.roleId).toLowerCase().includes(query) ||
          (p.name || "").toLowerCase().includes(query) ||
          (p.role || "").toLowerCase().includes(query) ||
          String(p.completed).toLowerCase().includes(query) ||
          String(p.payable).toLowerCase().includes(query) ||
          String(p.paid).toLowerCase().includes(query) ||
          String(p.pending).toLowerCase().includes(query);

        if (personMatch) {
          return true;
        }

        // Case/payment-level fields
        return p.cases.some(
          (c) =>
            String(c.paymentId).toLowerCase().includes(query) ||
            String(c.id).toLowerCase().includes(query) ||
            (c.orderId || "").toLowerCase().includes(query) ||
            (c.desc || "").toLowerCase().includes(query) ||
            String(c.qty).toLowerCase().includes(query) ||
            String(c.amount).toLowerCase().includes(query) ||
            (c.date || "").toLowerCase().includes(query) ||
            (c.status || "").toLowerCase().includes(query)
        );
      });
    }

    return result;
  }, [people, roleFilter, selectedName, search]);

  // -------------------------------------------------------------------------
  // Totals
  // -------------------------------------------------------------------------

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, p) => {
        acc.payable += p.payable;
        acc.paid += p.paid;
        acc.pending += p.pending;

        return acc;
      },
      {
        payable: 0,
        paid: 0,
        pending: 0,
      }
    );
  }, [filtered]);

  // -------------------------------------------------------------------------
  // Selected Person
  // -------------------------------------------------------------------------

  const selectedOriginal =
    people.find((p) => `${p.userId}_${p.roleId}` === selectedId) || null;

  const selected = useMemo(() => {
    if (!selectedOriginal) return null;

    return selectedOriginal;
  }, [selectedOriginal]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  function openDetail(userId: number, roleId: number) {
    setSelectedId(`${userId}_${roleId}`);
    setView("detail");
  }

  function goBack() {
    setView("list");
    setSelectedId(null);
  }

  const markCasePaid = async (paymentId: number) => {
    try {
      setLoading(true);

      await apiClient.post("/Payment/payment-tracking/pay", {
        startDate: startDate,
        endDate: endDate,
      });

      await loadPaymentTracking();
    } catch (error) {
      console.error("Payment failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const payPersonByDateRange = async (person: Person) => {
    if (!startDate || !endDate) {
      await Swal.fire({
        icon: "warning",
        title: "Date Range Required",
        text: "Please select a date range first.",
        confirmButtonText: "OK",
      });
      return;
    }

    const pendingCases = person.cases.filter((c) => {
      if (c.status !== "Pending" || !c.rawDate) {
        return false;
      }

      return c.rawDate >= startDate && c.rawDate <= endDate;
    });

    if (pendingCases.length === 0) {
      await Swal.fire({
        icon: "info",
        title: "No Pending Payments",
        text: `No pending payments found for ${person.name} from ${startDate} to ${endDate}.`,
        confirmButtonText: "OK",
      });
      return;
    }

    const totalAmount = pendingCases.reduce(
      (sum, c) => sum + Number(c.amount),
      0
    );

    const result = await Swal.fire({
      icon: "question",
      title: "Confirm Payment",
      html: `
    <div class="payment-confirm-modal">

      <div class="payment-confirm-intro">
        <div class="payment-confirm-icon">
          <i class="bi bi-credit-card-2-front"></i>
        </div>

        <div class="payment-confirm-message">
          <h6>Ready to mark payment as paid?</h6>
          <p>
            Please review the payment details before continuing.
          </p>
        </div>
      </div>

      <div class="payment-summary-card">

        <div class="payment-summary-header">
          <div>
            <span class="payment-summary-label">PAYMENT SUMMARY</span>
            <span class="payment-summary-subtitle">
              ${pendingCases.length} pending payment${pendingCases.length !== 1 ? "s" : ""
        }
            </span>
          </div>

          <div class="payment-summary-status">
            <i class="bi bi-clock-history"></i>
            Pending
          </div>
        </div>

        <div class="payment-person-row">
          <div class="payment-person-avatar">
            ${(person.name || "P").charAt(0).toUpperCase()}
          </div>

          <div class="payment-person-info">
            <span class="payment-field-label">Name</span>
            <strong>${person.name}</strong>
          </div>
        </div>

        <div class="payment-amount-box">
          <div class="payment-amount-content">
            <span class="payment-field-label">Total Amount</span>
            <strong>₹${totalAmount.toFixed(2)}</strong>
          </div>

          <div class="payment-amount-icon">
            <i class="bi bi-wallet2"></i>
          </div>
        </div>

        <div class="payment-details-grid">

          <div class="payment-detail-item">
            <div class="payment-detail-icon">
              <i class="bi bi-receipt"></i>
            </div>
            <div>
              <span class="payment-field-label">Payments</span>
              <strong>${pendingCases.length}</strong>
            </div>
          </div>

          <div class="payment-detail-item">
            <div class="payment-detail-icon">
              <i class="bi bi-calendar3"></i>
            </div>
            <div>
              <span class="payment-field-label">Period</span>
        <strong>
  ${formatPeriodDate(startDate)} - ${formatPeriodDate(endDate)}
</strong>
            </div>
          </div>

        </div>

      </div>

      <div class="payment-confirm-note">
        <i class="bi bi-info-circle"></i>
        <span>
          Once confirmed, these payments will be marked as
          <strong>Paid</strong>.
        </span>
      </div>

    </div>
  `,

      showCancelButton: true,

      confirmButtonText: `
    <i class="bi bi-check2-circle"></i>
    Yes, Mark as Paid
  `,

      cancelButtonText: `
    <i class="bi bi-x-lg"></i>
    Cancel
  `,

      reverseButtons: true,
      focusCancel: true,

      customClass: {
        popup: "payment-confirm-popup",
        title: "payment-confirm-title",
        htmlContainer: "payment-confirm-html",
        confirmButton: "payment-confirm-btn",
        cancelButton: "payment-cancel-btn2",
        actions: "payment-confirm-actions",
      },

      buttonsStyling: false,

      showCloseButton: true,

      width: "520px",

      padding: "0",

      backdrop: `
    rgba(15, 23, 42, 0.55)
  `,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      Swal.fire({
        title: "Processing Payment...",
        text: "Please wait while the payment is being processed.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await apiClient.post("/Payment/payment-tracking/pay", {
        userID: person.userId,
        roleID: person.roleId,
        startDate: startDate,
        endDate: endDate,
      });

      await loadPaymentTracking(startDate, endDate);

      await Swal.fire({
        icon: "success",
        title: "Payment Successful",
        html: `<strong>₹${totalAmount.toFixed(
          2
        )}</strong> paid successfully for <strong>${person.name
          }</strong>. <br/> <small>${pendingCases.length
          } payment(s) marked as paid.</small>`,
        confirmButtonText: "Done",
      });
    } catch (error) {
      console.error("Person payment failed:", error);

      await Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: "Payment failed. Please check the payment tracking and try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // OPEN / CLOSE PAYMENT MODAL
  // -------------------------------------------------------------------------

  function openPaymentModal(person: Person) {
    setModalPerson(person);
    setShowPaymentModal(true);
  }

  function closePaymentModal() {
    setShowPaymentModal(false);
    setModalPerson(null);
  }

  // -------------------------------------------------------------------------
  // Team Payment DataTable Columns
  // -------------------------------------------------------------------------
 const teamPaymentColumns: TableColumn<Person>[] = useMemo(
  () => [
    {
      name: "Name",
      width: "20%",
      grow: 0,
      cell: (p) => (
        <div className="pt-person">
          <div className="pt-avatar">
            {getInitials(p.name)}
          </div>

          <div className="pt-person-info">
            <span
              className="pt-person-name"
              title={p.name}
            >
              {p.name}
            </span>

            <div className="pt-person-meta">
              {p.cases.length} payment cases
            </div>
          </div>
        </div>
      ),
    },

    {
      name: "Role",
      selector: (p) => p.role,
      sortable: true,
      center: true,
      width: "20%",
      grow: 0,

      cell: (p) => (
        <span className={`pt-role-badge ${getRoleClass(p.role)}`}>
          {p.role}
        </span>
      ),
    },

    {
      name: "Completed",
      selector: (p) => p.completed,
      sortable: true,
      center: true,
      width: "20%",
      grow: 0,

      cell: (p) => (
        <div className="pt-completed-cell">
          <span className="pt-completed-value">
            {p.completed}
          </span>
        </div>
      ),
    },

    {
      name: "Pending",
      selector: (p) => p.pending,
      sortable: true,
      center: true,
      width: "20%",
      grow: 0,

      cell: (p) => (
        <div className="pt-pending-cell">
          <span
            className={
              p.pending === 0
                ? "pt-zero"
                : "pt-pending"
            }
          >
            {money(p.pending)}
          </span>
        </div>
      ),
    },

    {
      name: "Action",
      center: true,
      width: "20%",
      grow: 0,

      cell: (p) => (
        <div className="pt-actions">
          <button
            type="button"
            className="pt-view-btn"
            onClick={() => openPaymentModal(p)}
          >
            <span>View</span>
            <ChevronRight size={15} />
          </button>

          {p.pending > 0 && (
            <button
              type="button"
              className="pt-pay-btn"
              onClick={() => {
                if (!rangeStart || !rangeEnd) {
                  Swal.fire({
                    icon: "warning",
                    title: "Date Range Required",
                    text: "Please select a date range first.",
                    confirmButtonText: "OK",
                  });

                  return;
                }

                payPersonByDateRange(p);
              }}
              disabled={loading}
            >
              <CreditCard size={14} />
              <span>Mark as paid</span>
            </button>
          )}
        </div>
      ),
    },
  ],
  [rangeStart, rangeEnd, loading]
);
  // const teamPaymentColumns: TableColumn<Person>[] = useMemo(
  //   () => [
  //     {
  //       name: "Name",

  //       cell: (p) => (
  //         <div className="pt-person">
  //           <div className="pt-avatar">
  //             {getInitials(p.name)}
  //           </div>

  //           <div className="pt-person-info">
  //             <span
  //               className="pt-person-name"
  //               title={p.name}
  //             >
  //               {p.name}
  //             </span>

  //             <div className="pt-person-meta">
  //               {p.cases.length} payment cases
  //             </div>
  //           </div>
  //         </div>
  //       ),
  //     },

  //     {
  //       name: "Role",
  //       selector: (p) => p.role,
  //       sortable: true,
  //       center: true,

  //       cell: (p) => (
  //         <span className={`pt-role-badge ${getRoleClass(p.role)}`}>
  //           {p.role}
  //         </span>
  //       ),
  //     },

  //     {
  //       name: "Completed",
  //       selector: (p) => p.completed,
  //       sortable: true,
  //       center: true,

  //       cell: (p) => (
  //         <div className="pt-completed-cell">
  //           <span className="pt-completed-value">{p.completed}</span>
  //         </div>
  //       ),
  //     },

  //     {
  //       name: "Pending",
  //       selector: (p) => p.pending,
  //       sortable: true,
  //       right: true,

  //       cell: (p) => (
  //         <div className="pt-pending-cell">
  //           <span className={p.pending === 0 ? "pt-zero" : "pt-pending"}>
  //             {money(p.pending)}
  //           </span>
  //         </div>
  //       ),
  //     },

  //     {
  //       name: "Action",
  //       center: true,

  //       cell: (p) => (
  //         <div className="pt-actions">
  //           <button
  //             type="button"
  //             className="pt-view-btn"
  //             onClick={() => openPaymentModal(p)}
  //           >
  //             <span>View</span>
  //             <ChevronRight size={15} />
  //           </button>

  //           {p.pending > 0 && (
  //             <button
  //               type="button"
  //               className="pt-pay-btn"
  //               onClick={() => {
  //                 if (!rangeStart || !rangeEnd) {
  //                   Swal.fire({
  //                     icon: "warning",
  //                     title: "Date Range Required",
  //                     text: "Please select a date range first.",
  //                     confirmButtonText: "OK",
  //                   });
  //                   return;
  //                 }

  //                 payPersonByDateRange(p);
  //               }}
  //               disabled={loading}
  //             >
  //               <CreditCard size={14} />
  //               <span>Mark as paid</span>
  //             </button>
  //           )}
  //         </div>
  //       ),
  //     },
  //   ],
  //   [rangeStart, rangeEnd, loading]
  // );

  // -------------------------------------------------------------------------
  // Case DataTable Columns
  // -------------------------------------------------------------------------

  const caseColumns: TableColumn<CaseItem>[] = useMemo(
    () => [
      {
        name: "Case",
        selector: (c) => c.id,
        sortable: true,
        minWidth: "130px",

        cell: (c) => <span className="pt-case-id">{c.id}</span>,
      },

      {
        name: "Description",
        selector: (c) => c.desc,
        sortable: true,
        grow: 2,
        minWidth: "280px",

        cell: (c) => (
          <span className="pt-case-description">{c.desc}</span>
        ),
      },

      {
        name: "Qty",
        selector: (c) => c.qty,
        sortable: true,
        right: true,
        width: "80px",
        grow: 0,

        cell: (c) => <strong>{c.qty}</strong>,
      },

      {
        name: "Date",
        selector: (c) => c.date,
        sortable: true,
        width: "110px",
        grow: 0,

        cell: (c) => <span className="pt-case-date">{c.date}</span>,
      },

      {
        name: "Amount",
        selector: (c) => c.amount,
        sortable: true,
        right: true,
        width: "130px",
        grow: 0,

        cell: (c) => <strong>{money(c.amount)}</strong>,
      },

      {
        name: "Status",
        right: true,
        minWidth: "190px",

        cell: (c) => {
          const isPaid = c.status === "Paid";

          return isPaid ? (
            <span className="pt-status-pill paid">
              <CheckCircle2 size={13} />
              Paid
            </span>
          ) : (
            <div className="pt-actions">
              <span className="pt-status-pill pending">
                <Clock3 size={13} />
                Pending
              </span>

              <button
                className="pt-case-pay"
                onClick={() => markCasePaid(c.paymentId)}
              >
                <CreditCard size={12} />
                Pay
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  // -------------------------------------------------------------------------
  // PAYMENT MODAL DATATABLE COLUMNS
  // -------------------------------------------------------------------------

  const paymentModalColumns: TableColumn<CaseItem>[] = useMemo(
    () => [
      {
        name: "Case ID",
        selector: (c) => c.id,
        sortable: true,
        minWidth: "150px",

        cell: (c) => <span className="pt-case-id">{c.id}</span>,
      },

      {
        name: "Material",
        selector: (c) => c.desc,
        sortable: true,
        grow: 2,
        minWidth: "280px",

        cell: (c) => (
          <span className="pt-case-description">{c.desc}</span>
        ),
      },
      {
        name: "Date",
        selector: (c) => c.date,
        sortable: true,
        width: "150px",
        grow: 0,

        cell: (c) => <span className="pt-case-date">{c.date}</span>,
      },

      {
        name: "Payable",
        selector: (c) => c.amount,
        sortable: true,
        right: true,
        width: "150px",
        grow: 0,

        cell: (c) => <strong>{money(c.amount)}</strong>,
      },

      {
        name: "Paid",
        selector: (c) => (c.status === "Paid" ? c.amount : 0),
        sortable: true,
        right: true,
        width: "150px",
        grow: 0,

        cell: (c) => {
          const isPaid = c.status === "Paid";

          return (
            <span className={isPaid ? "pt-paid" : "pt-modal-not-paid"}>
              {money(isPaid ? c.amount : 0)}
            </span>
          );
        },
      },
    ],
    []
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="pt-root">
      <div className="pt-shell">
        {view === "list" ? (
          <>
            {/* HEADER */}

            <div className="pt-header">
              <div className="pt-brand-row">
                <div>
                  <h1 className="pt-title">Payment Tracking</h1>

                  <p className="pt-subtitle">
                    Manage team payables, completed work and pending
                    payments.
                  </p>
                </div>
              </div>
            </div>

            {/* SUMMARY */}

            <div className="pt-summary-grid py-3">
              {/* TOTAL PAYABLE */}

              <div className="pt-summary-card">
                <div className="pt-summary-top">
                  <span className="pt-summary-label">Total payable</span>

                  <div className="pt-summary-icon">
                    <IndianRupee size={18} />
                  </div>
                </div>

                <p className="pt-summary-value">{money(totals.payable)}</p>

                <div className="pt-summary-bottom">
                  <Users size={13} />
                  {filtered.length} team members
                </div>
              </div>

              {/* PENDING */}

              <div className="pt-summary-card">
                <div className="pt-summary-top">
                  <span className="pt-summary-label">Pending payment</span>

                  <div className="pt-summary-icon orange">
                    <Clock3 size={18} />
                  </div>
                </div>

                <p className="pt-summary-value">{money(totals.pending)}</p>

                <div className="pt-summary-bottom ">
                  <Clock3 size={13} />
                  Awaiting settlement
                </div>
              </div>
            </div>

            {/* TEAM PAYMENTS TABLE */}

            <div className="premium-card">
              <div className="pt-toolbar row py-2">
                <div className="col-md-4">
                  <div className="pt-filters">
                    {ROLES.map((role) => (
                      <button
                        key={role}
                        className={
                          "pt-filter" +
                          (roleFilter === role ? " active" : "")
                        }
                        onClick={() => {
                          setRoleFilter(role);
                          setSelectedName("");
                        }}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-md-7">
                  <div className="d-flex row">
                    <div className="col-md-6">
                      <div className="pt-date-range">
                        <CalendarRange size={16} />

                        <DatePicker
                          selectsRange
                          startDate={rangeStart}
                          endDate={rangeEnd}
                          onChange={(update) => {
                            const [newStart, newEnd] = update as [
                              Date | null,
                              Date | null
                            ];

                            setDateRange([newStart, newEnd]);

                            // Call API only after both dates are selected
                            if (newStart && newEnd) {
                              const newStartDate = toISODate(newStart);
                              const newEndDate = toISODate(newEnd);

                              loadPaymentTracking(newStartDate, newEndDate);
                            }
                          }}
                          isClearable
                          placeholderText="Select date range"
                          dateFormat="dd MMM yyyy"
                          className="pt-date-input"
                          wrapperClassName="pt-date-input-wrapper"
                          maxDate={new Date()}
                        />

                        {(rangeStart || rangeEnd) && (
                          <button
                            type="button"
                            className="pt-date-clear"
                            onClick={clearDateRange}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="pt-name-dropdown">
                        <Select
                          classNamePrefix="payment-select"
                          options={nameOptions}
                          components={nameSelectComponents}
                          value={
                            nameOptions.find(
                              (option) => option.value === selectedName
                            ) || null
                          }
                          onChange={(option) => {
                            setSelectedName(option?.value || "");
                          }}
                          isSearchable
                          isClearable
                          placeholder="Select team member..."
                          noOptionsMessage={() => "No team member found"}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-table-head">
                <h2 className="pt-section-title">
                  Team payments
                  <span className="pt-section-count">
                    {filtered.length} members
                  </span>
                </h2>
              </div>

              <div
                className="pt-team-table-scroll"
                style={{ width: "100%", overflowX: "auto" }}
              >
                <DataTable
                  columns={teamPaymentColumns}
                  data={filtered}
                  customStyles={teamTableStyles}
                  pagination
                  highlightOnHover
                  striped
                  responsive
                  fixedHeader
                  fixedHeaderScrollHeight="500px"
                  persistTableHead
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 20, 30]}
                  // noDataComponent={
                  //   <div className="pt-empty">
                  //     <div className="pt-empty-icon">
                  //       <Search size={20} />
                  //     </div>

                  //     <h3 className="pt-empty-title">
                  //       No team members found
                  //     </h3>

                  //     <p className="pt-empty-text">
                  //       Try changing the role filter, date range or search
                  //       term.
                  //     </p>
                  //   </div>
                  // }
                />
              </div>
            </div>
          </>
        ) : (
          selected && (
            <>
              {/* BACK */}

              <button className="pt-back" onClick={goBack}>
                <ArrowLeft size={16} />
                Back to payments
              </button>

              {/* DETAIL HEADER */}

              <div className="pt-detail-top">
                <div className="pt-detail-profile">
                  <div className="pt-detail-avatar">
                    {getInitials(selected.name)}
                  </div>

                  <div>
                    <h2 className="pt-detail-name">
                      {selected.name}

                      <span className="pt-detail-role">
                        <span
                          className={
                            "pt-role-badge " + getRoleClass(selected.role)
                          }
                        >
                          {selected.role}
                        </span>
                      </span>
                    </h2>

                    <p className="pt-detail-sub">
                      {selected.cases.length} cases · {selected.completed}{" "}
                      units completed
                    </p>
                  </div>
                </div>

                <div className="pt-detail-total">
                  <div className="pt-detail-total-label">Total payable</div>

                  <div className="pt-detail-total-value">
                    {money(selected.payable)}
                  </div>
                </div>
              </div>

              {/* DETAIL SUMMARY */}

              <div className="pt-detail-summary">
                <div className="pt-detail-stat">
                  <div className="pt-detail-stat-label">Total payable</div>

                  <div className="pt-detail-stat-value">
                    {money(selected.payable)}
                  </div>
                </div>

                <div className="pt-detail-stat">
                  <div className="pt-detail-stat-label">Paid amount</div>

                  <div className="pt-detail-stat-value pt-paid">
                    {money(selected.paid)}
                  </div>
                </div>

                <div className="pt-detail-stat">
                  <div className="pt-detail-stat-label">Pending amount</div>

                  <div className="pt-detail-stat-value pt-pending">
                    {money(selected.pending)}
                  </div>
                </div>
              </div>

              {/* PAYMENT CASES */}

              <div className="premium-card">
                <div className="pt-table-head">
                  <h2 className="pt-section-title">
                    Payment cases
                    <span className="pt-section-count">
                      {selected.cases.length} cases
                    </span>
                  </h2>
                </div>

                <div
                  className="pt-case-table-scroll"
                  style={{ width: "100%", overflowX: "auto" }}
                >
                  <DataTable
                    columns={caseColumns}
                    data={selected.cases}
                    // customStyles={caseTableStyles}
                    pagination
                    highlightOnHover
                    striped
                    responsive
                    fixedHeader
                    fixedHeaderScrollHeight="500px"
                    persistTableHead
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[5, 10, 20, 30]}
                    noDataComponent={
                      <div className="pt-empty">
                        <div className="pt-empty-icon">
                          <Search size={20} />
                        </div>

                        <h3 className="pt-empty-title">
                          No payment cases found
                        </h3>

                        <p className="pt-empty-text">
                          There are no payment cases available.
                        </p>
                      </div>
                    }
                  />
                </div>
              </div>
            </>
          )
        )}
      </div>

      {/* ================================================================= */}
      {/* PAYMENT MODAL */}
      {/* ================================================================= */}

      {showPaymentModal && modalPerson && (
        <div className="pt-modal-overlay" onClick={closePaymentModal}>
          <div
            className="pt-payment-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* MODAL HEADER */}

            <div className="pt-modal-header">
              <div>
                <h2 className="pt-modal-title">{modalPerson.name}</h2>

                <p className="pt-modal-subtitle">
                  Payment details · {modalPerson.role}
                </p>
              </div>

              <button
                type="button"
                className="pt-modal-close"
                onClick={closePaymentModal}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            {/* MODAL SUMMARY */}

            <div className="pt-modal-summary">
              {/* PAYABLE */}

              <div className="pt-modal-summary-card">
                <span>Total Payable</span>

                <strong>{money(modalPerson.payable)}</strong>
              </div>

              {/* PAID */}

              <div className="pt-modal-summary-card">
                <span>Total Paid</span>

                <strong className="pt-modal-paid">
                  {money(
                    modalPerson.cases.reduce(
                      (sum, item) =>
                        sum + (item.status === "Paid" ? item.amount : 0),
                      0
                    )
                  )}
                </strong>
              </div>

              {/* PENDING */}

              <div className="pt-modal-summary-card">
                <span>Pending</span>

                <strong className="pt-modal-pending">
                  {money(
                    modalPerson.cases.reduce(
                      (sum, item) =>
                        sum + (item.status === "Pending" ? item.amount : 0),
                      0
                    )
                  )}
                </strong>
              </div>
            </div>

            {/* MODAL DATATABLE */}

            <div
              className="pt-modal-table"
              style={{ width: "100%", overflowX: "auto" }}
            >
              <DataTable
                columns={paymentModalColumns}
                data={modalPerson.cases}
                // customStyles={modalTableStyles}
                pagination
                paginationPerPage={5}
                paginationRowsPerPageOptions={[5, 10, 20]}
                paginationComponentOptions={{
                  rowsPerPageText: "Rows per page:",
                  rangeSeparatorText: "of",
                  noRowsPerPage: false,
                  selectAllRowsItem: false,
                }}
                highlightOnHover
                striped
                responsive
                fixedHeader
                fixedHeaderScrollHeight="300px"
                persistTableHead
                noDataComponent={
                  <div className="pt-empty">
                    <div className="pt-empty-icon">
                      <Search size={20} />
                    </div>

                    <h3 className="pt-empty-title">
                      No payment cases found
                    </h3>

                    <p className="pt-empty-text">
                      There are no payment cases available.
                    </p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
